/**
 * FrostedERVModal — Apple Home 風格毛玻璃 全熱交換機 控制面板
 *
 * 設計語言：
 * - Teal/Emerald tint → ERV 專用色調（新鮮空氣 = 青綠色）
 * - CO2 ring dial → CO2 ppm + 空氣品質等級
 * - Airflow status card → Indoor/Outdoor temp + CO2
 * - Mode: Auto / Ventilate / Recovery / Bypass
 * - Fan Speed: Low / Med / High / Auto
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './FrostedERVModal.css'

// ---------------------------------------------------------------------------
// Section definitions
// ---------------------------------------------------------------------------

export const ERV_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'dial', label: 'CO2 Dial', icon: 'co2', slots: [
    { id: 'dial-arc', label: 'Arc Stroke', editableProps: ['color', 'glow'] },
    { id: 'dial-co2', label: 'CO2 Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'dial-label', label: 'AQ Label', editableProps: ['fontSize', 'color'] },
  ]},
  { id: 'mode', label: 'Mode', icon: 'auto_mode', slots: [
    { id: 'mode-bar', label: 'Mode Bar Container', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'mode-btn', label: 'Mode Button', editableProps: ['background', 'color', 'borderRadius', 'fontSize'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'fan', label: 'Fan Speed', icon: 'mode_fan', slots: [
    { id: 'fan-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'letterSpacing'] },
    { id: 'fan-btn', label: 'Speed Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'airflow', label: 'Airflow Status', icon: 'swap_horiz', slots: [] },
  { id: 'chart', label: 'CO2 History', icon: 'show_chart', slots: [] },
]

const DEFAULT_SECTION_ORDER = ERV_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODES = [
  { id: 'auto', label: 'Auto', icon: 'auto_mode' },
  { id: 'ventilate', label: 'Ventilate', icon: 'air' },
  { id: 'recovery', label: 'Recovery', icon: 'whatshot' },
  { id: 'bypass', label: 'Bypass', icon: 'open_in_new' },
] as const

const FAN_SPEEDS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Med' },
  { id: 'high', label: 'High' },
  { id: 'auto', label: 'Auto' },
] as const

const CO2_MAX_RING = 2000

function co2Level(co2: number): { label: string; color: string } {
  if (co2 <= 600) return { label: '空氣品質 優良', color: 'rgba(74,222,128,0.85)' }
  if (co2 <= 1000) return { label: '空氣品質 良好', color: 'rgba(250,204,21,0.85)' }
  if (co2 <= 1500) return { label: '空氣品質 偏差', color: 'rgba(251,146,60,0.85)' }
  return { label: '空氣品質 危險', color: 'rgba(239,68,68,0.85)' }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}
function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}
function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys)
  return v != null ? String(v) : ''
}

// Ring — 270° arc
const RING_R = 85, RING_CX = 100, RING_CY = 100
const RING_CIRC = 2 * Math.PI * RING_R
const RING_ARC_RATIO = 0.75
const RING_TRACK_OFFSET = RING_CIRC * (1 - RING_ARC_RATIO)

function co2ToDashOffset(co2: number): number {
  const ratio = Math.max(0, Math.min(1, co2 / CO2_MAX_RING))
  return RING_CIRC - ratio * RING_CIRC * RING_ARC_RATIO
}
const DEG = Math.PI / 180
function co2ToDotPos(co2: number): { x: number; y: number } {
  const ratio = Math.max(0, Math.min(1, co2 / CO2_MAX_RING))
  const rad = (135 + ratio * 270) * DEG
  return { x: RING_CX + RING_R * Math.cos(rad), y: RING_CY + RING_R * Math.sin(rad) }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FrostedERVModal({
  device, deviceState, onCommand, onClose, isEditorMode, cssOverrides,
  sectionOrder, sectionVisible, onSectionContextMenu, highlightedSection, credentials,
}: DeviceModalTemplateProps) {

  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isEditorMode || !onSectionContextMenu) return
    const sectionEl = (event.target as HTMLElement)?.closest('[data-modal-section]') as HTMLElement | null
    if (!sectionEl) return
    event.preventDefault(); event.stopPropagation()
    const sectionId = sectionEl.dataset.modalSection || ''
    if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
  }, [isEditorMode, onSectionContextMenu])

  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    return [...order, ...DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))]
  }, [sectionOrder])

  const isSectionVisible = useCallback((id: string) => sectionVisible?.[id] !== false, [sectionVisible])

  // ---- Device state ----
  const isOn = Boolean(getAttr(deviceState, 'on'))
  const co2 = numAttr(deviceState, 'co2', 'CO2', 'carbonDioxide') ?? 0
  const indoorTemp = numAttr(deviceState, 'temperatureAmbientCelsius', 'indoorTemperature', 'temperature')
  const outdoorTemp = numAttr(deviceState, 'outdoorTemperature', 'supplyTemperature')
  const currentMode = strAttr(deviceState, 'currentModeSettings', 'mode') || 'auto'
  const currentFanSpeed = strAttr(deviceState, 'currentFanSpeedSetting', 'fanSpeed') || 'auto'

  // ---- Settings ----
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label); setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ---- Rename ----
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])
  const startRename = useCallback(() => {
    setRenameValue(displayName); setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])
  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) { setIsRenaming(false); return }
    setDisplayName(trimmed); setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ---- Optimistic UI ----
  const [pendingMode, setPendingMode] = useState<string | null>(null)
  const [pendingFan, setPendingFan] = useState<string | null>(null)
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const displayMode = pendingMode ?? currentMode
  const displayFan = pendingFan ?? currentFanSpeed
  const displayPower = pendingPower ?? isOn

  useEffect(() => { if (pendingMode !== null && pendingMode === currentMode) setPendingMode(null) }, [currentMode, pendingMode])
  useEffect(() => { if (pendingFan !== null && pendingFan === currentFanSpeed) setPendingFan(null) }, [currentFanSpeed, pendingFan])
  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])

  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  const togglePower = useCallback(() => {
    if (isEditorMode) return
    const next = !displayPower; setPendingPower(next); execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])

  const setModeCmd = useCallback((mode: string) => {
    if (isEditorMode) return; setPendingMode(mode); execCmd('setMode', { mode })
  }, [isEditorMode, execCmd])

  const setFanSpeedCmd = useCallback((speed: string) => {
    if (isEditorMode) return; setPendingFan(speed); execCmd('setFanSpeed', { fanSpeed: speed })
  }, [isEditorMode, execCmd])

  // ---- Ring ----
  const displayCO2 = displayPower ? co2 : 0
  const valueDashOffset = co2ToDashOffset(displayCO2)
  const dotPos = co2ToDotPos(displayCO2)
  const aqLevel = co2Level(co2)

  const stateClass = displayPower ? 'erv-frosted--on' : 'erv-frosted--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null
    switch (sectionId) {
      case 'dial':
        return (
          <div key="dial" className={`erv-frosted__dial${hl('dial')}`} data-modal-section="dial">
            <svg viewBox="0 0 200 200" className="erv-frosted__ring-svg">
              <defs>
                <linearGradient id="ervTealGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#2dd4bf"/><stop offset="100%" stopColor="#5eead4"/>
                </linearGradient>
              </defs>
              <circle cx={RING_CX} cy={RING_CY} r={RING_R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={16} strokeLinecap="round" strokeDasharray={RING_CIRC} strokeDashoffset={RING_TRACK_OFFSET} transform={`rotate(135,${RING_CX},${RING_CY})`}/>
              <circle cx={RING_CX} cy={RING_CY} r={RING_R} fill="none" stroke="url(#ervTealGrad)" strokeWidth={16} strokeLinecap="round" strokeDasharray={RING_CIRC} strokeDashoffset={displayPower ? valueDashOffset : RING_CIRC} transform={`rotate(135,${RING_CX},${RING_CY})`} style={{ transition: 'stroke-dashoffset 0.3s ease' }}/>
              {displayPower && <circle cx={dotPos.x} cy={dotPos.y} r={7} fill="#fff" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}/>}
            </svg>
            <div className="erv-frosted__dial-text">
              {displayPower ? (
                <>
                  <div><span className="erv-frosted__co2-value">{co2}</span><span className="erv-frosted__co2-unit">ppm</span></div>
                  <div className="erv-frosted__co2-label" style={{ color: aqLevel.color }}>{aqLevel.label}</div>
                </>
              ) : <span className="erv-frosted__off-text">OFF</span>}
            </div>
            <button className="erv-frosted__power-tap" onClick={togglePower} aria-label={displayPower ? 'Turn off' : 'Turn on'}/>
          </div>
        )
      case 'mode':
        return (
          <div key="mode" className={`erv-frosted__modes${hl('mode')}`} data-modal-section="mode">
            {MODES.map(m => (
              <button key={m.id} className={`erv-frosted__mode-btn${displayMode === m.id ? ' active' : ''}`} onClick={() => setModeCmd(m.id)}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{m.icon}</span> {m.label}
              </button>
            ))}
          </div>
        )
      case 'fan':
        return (
          <div key="fan" className={`erv-frosted__fan${hl('fan')}`} data-modal-section="fan">
            <div className="erv-frosted__fan-label">Fan Speed</div>
            <div className="erv-frosted__fan-buttons">
              {FAN_SPEEDS.map(f => (
                <button key={f.id} className={`erv-frosted__fan-btn${displayFan === f.id ? ' active' : ''}`} onClick={() => setFanSpeedCmd(f.id)}>{f.label}</button>
              ))}
            </div>
          </div>
        )
      case 'airflow':
        return (
          <div key="airflow" className={`erv-frosted__airflow-card${hl('airflow')}`} data-modal-section="airflow">
            <div className="erv-frosted__airflow-title">
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>swap_horiz</span> AIRFLOW STATUS
            </div>
            <div className="erv-frosted__airflow-row">
              <div className="erv-frosted__airflow-item">
                <div className="erv-frosted__airflow-item-label">Indoor</div>
                <div><span className="erv-frosted__airflow-item-value">{indoorTemp?.toFixed(1) ?? '--'}</span><span className="erv-frosted__airflow-item-unit">°C</span></div>
              </div>
              <div className="erv-frosted__airflow-item">
                <div className="erv-frosted__airflow-item-label">Outdoor</div>
                <div><span className="erv-frosted__airflow-item-value">{outdoorTemp?.toFixed(1) ?? '--'}</span><span className="erv-frosted__airflow-item-unit">°C</span></div>
              </div>
              <div className="erv-frosted__airflow-item">
                <div className="erv-frosted__airflow-item-label">CO₂</div>
                <div><span className="erv-frosted__airflow-item-value">{co2 || '--'}</span><span className="erv-frosted__airflow-item-unit">ppm</span></div>
              </div>
            </div>
          </div>
        )
      case 'chart':
        return (
          <div key="chart" className={`erv-frosted__chart-section${hl('chart')}`} data-modal-section="chart">
            <div className="erv-frosted__chart-controls">
              <span className="erv-frosted__chart-ctrl"><span className="material-symbols-outlined" style={{ fontSize: 18 }}>schedule</span> 24H</span>
              <span className="erv-frosted__chart-ctrl"><span className="material-symbols-outlined" style={{ fontSize: 18 }}>co2</span> CO₂</span>
            </div>
            <div className="erv-frosted__chart-area">
              <svg viewBox="0 0 340 80" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                <defs>
                  <linearGradient id="ervChartFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(45,212,191,0.3)"/><stop offset="100%" stopColor="rgba(45,212,191,0.02)"/>
                  </linearGradient>
                </defs>
                <path d="M0,55 C20,52 40,48 60,42 C80,38 100,32 120,28 C140,32 160,38 180,42 C200,45 220,50 240,48 C260,44 280,38 300,35 C320,32 330,30 340,28" fill="none" stroke="rgba(45,212,191,0.7)" strokeWidth="2"/>
                <path d="M0,55 C20,52 40,48 60,42 C80,38 100,32 120,28 C140,32 160,38 180,42 C200,45 220,50 240,48 C260,44 280,38 300,35 C320,32 330,30 340,28 L340,80 L0,80 Z" fill="url(#ervChartFill)"/>
              </svg>
            </div>
            <div className="erv-frosted__chart-stats">
              <span>Now {co2 || '--'}</span><span>Max --</span><span>Min --</span>
            </div>
          </div>
        )
      default: return null
    }
  }

  return (
    <div className={`erv-frosted ${stateClass}`} data-modal-template-id="erv-frosted" onContextMenu={handleContextMenu}>
      {cssOverrides && <style>{cssOverrides}</style>}
      <div className="erv-frosted__header" data-modal-section="_modal">
        <button className="erv-frosted__back" onClick={onClose} aria-label="Back">‹</button>
        <span className="erv-frosted__name">{displayName}</span>
        <button className={`erv-frosted__power-btn${displayPower ? ' erv-frosted__power-btn--on' : ''}`} onClick={togglePower} aria-label="Power">
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>power_settings_new</span>
        </button>
      </div>
      {orderedSections.map(id => renderSection(id))}
      <button className="erv-frosted__settings-btn" onClick={() => setShowSettings(true)} aria-label="Settings">
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>
      {showSettings && (
        <div className="erv-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="erv-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="erv-frosted__settings-header">
              <span className="erv-frosted__settings-title">設定</span>
              <button className="erv-frosted__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>
            <div className="erv-frosted__settings-section">
              <div className="erv-frosted__settings-section-title">裝置名稱</div>
              {isRenaming ? (
                <div className="erv-frosted__settings-row">
                  <input ref={renameInputRef} className="erv-frosted__rename-input" value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename} maxLength={40}/>
                </div>
              ) : (
                <div className="erv-frosted__settings-row">
                  <span className="erv-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>{displayName}</span>
                  <button className="erv-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>
            <div className="erv-frosted__settings-section">
              <div className="erv-frosted__settings-section-title">裝置資訊</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="erv-frosted__settings-row">
                  <span className="erv-frosted__settings-label">{item.label}</span>
                  <span className="erv-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button className="erv-frosted__settings-copy" onClick={() => copyToClipboard(item.value, item.label)} title="Copy">
                      📋{copiedField === item.label && <span className="erv-frosted__copied-toast">Copied</span>}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
